// BillingCredits.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { billingAPI } from '../../api';

function BillingCredits() {
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [paymentModel, setPaymentModel] = useState('');
  const [autoTopup, setAutoTopup] = useState({
    enabled: false,
    threshold: 0,
    amount: 0
  });
  const [products, setProducts] = useState([]);
  const [usage, setUsage] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currency, setCurrency] = useState('NGN');
  const navigate = useNavigate();

  // Fetch billing data
  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    setIsLoading(true);
    try {
      // Fetch billing summary
      const summaryRes = await billingAPI.getSummary();
      if (summaryRes.success) {
        setBillingSummary(summaryRes.data);
        setPaymentModel(summaryRes.data.user?.payment_model || 'SUBSCRIPTION');
        setAutoTopup({
          enabled: summaryRes.data.user?.auto_topup_enabled || false,
          threshold: summaryRes.data.user?.auto_topup_threshold || 0,
          amount: summaryRes.data.user?.auto_topup_amount || 0
        });
      }

      // Fetch PAYG products
      const productsRes = await billingAPI.getProducts(true);
      if (productsRes.success) {
        setProducts(productsRes.data?.products || []);
      }

      // Fetch usage
      const usageRes = await billingAPI.getUsage('cbt_created');
      if (usageRes.success) {
        setUsage([usageRes.data]);
      }
    } catch (error) {
      console.error('Failed to load billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchaseCredits = async (productId) => {
    setIsProcessing(true);
    try {
      const response = await billingAPI.purchaseCredits(productId, currency);
      
      if (!response.success) {
        toast.error(response.error || 'Failed to purchase credits');
        setIsProcessing(false);
        return;
      }
      
      const invoice = response.data;
      
      if (invoice.payment_url) {
        // Redirect to payment gateway
        window.location.href = invoice.payment_url;
      } else {
        toast.success('Credits purchased successfully!');
        fetchBillingData();
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.data?.detail || error.message || 'Failed to purchase credits');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSwitchModel = async (newModel) => {
    if (!window.confirm(`Switch to ${newModel} payment model?`)) return;
    
    setIsProcessing(true);
    try {
      const response = await billingAPI.switchPaymentModel(newModel);
      
      if (!response.success) {
        toast.error(response.error || 'Failed to switch payment model');
        setIsProcessing(false);
        return;
      }
      
      setPaymentModel(newModel);
      toast.success(`Payment model switched to ${newModel}`);
      fetchBillingData();
    } catch (error) {
      console.error('Switch model error:', error);
      toast.error(error.data?.detail || error.message || 'Failed to switch payment model');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateAutoTopup = async () => {
    setIsProcessing(true);
    try {
      const response = await billingAPI.updateAutoTopup(
        autoTopup.enabled,
        autoTopup.threshold,
        autoTopup.amount
      );
      
      if (!response.success) {
        toast.error(response.error || 'Failed to update auto-topup');
        setIsProcessing(false);
        return;
      }
      
      toast.success('Auto-topup settings updated');
    } catch (error) {
      console.error('Auto-topup update error:', error);
      toast.error(error.data?.detail || error.message || 'Failed to update auto-topup');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading billing information...</p>
        </div>
      </div>
    );
  }

  const availableModels = ['SUBSCRIPTION', 'PAY_AS_YOU_GO', 'HYBRID'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Billing & Credits</h1>
          <p className="text-gray-600 mt-2">Manage your payment model and purchase credits</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Current Balance & Model */}
          <div className="lg:col-span-2 space-y-8">
            {/* Current Balance Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Current Balance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm">PAYG Credits</p>
                      <p className="text-3xl font-bold text-gray-800 mt-2">
                        {billingSummary?.user?.payg_credits || 0}
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        Expires: {billingSummary?.user?.payg_credits_expires_at ? 
                          new Date(billingSummary.user.payg_credits_expires_at).toLocaleDateString() : 
                          'Never'}
                      </p>
                    </div>
                    <div className="bg-blue-500 text-white p-3 rounded-full">
                      <i className="fas fa-coins text-2xl"></i>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm">Payment Model</p>
                      <p className="text-xl font-bold text-gray-800 mt-2 capitalize">
                        {paymentModel?.replace(/_/g, ' ').toLowerCase()}
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        Current active model
                      </p>
                    </div>
                    <div className="bg-green-500 text-white p-3 rounded-full">
                      <i className="fas fa-credit-card text-2xl"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Model Selection */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Payment Model</h3>
              <div className="space-y-4">
                {availableModels.map((model) => (
                  <div 
                    key={model}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 ${
                      paymentModel === model 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div>
                      <h4 className="font-bold text-gray-800">
                        {model.replace(/_/g, ' ')}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {model === 'SUBSCRIPTION' 
                          ? 'Monthly/Yearly subscription plan' 
                          : model === 'PAY_AS_YOU_GO' 
                          ? 'Pay only for what you use with credits'
                          : 'Combination of subscription and PAYG'}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      {paymentModel === model ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                          Active
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSwitchModel(model)}
                          disabled={isProcessing}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
                        >
                          Switch
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Auto-Topup Settings */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-800">Auto-Topup Settings</h3>
                <div className="flex items-center">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    autoTopup.enabled 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {autoTopup.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="autoTopupEnabled"
                    checked={autoTopup.enabled}
                    onChange={(e) => setAutoTopup({...autoTopup, enabled: e.target.checked})}
                    className="h-5 w-5 text-blue-600 rounded"
                  />
                  <label htmlFor="autoTopupEnabled" className="ml-3 text-gray-700">
                    Enable automatic credit top-up
                  </label>
                </div>

                {autoTopup.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        When credits fall below:
                      </label>
                      <input
                        type="number"
                        value={autoTopup.threshold}
                        onChange={(e) => setAutoTopup({...autoTopup, threshold: parseFloat(e.target.value)})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Add credits amount:
                      </label>
                      <input
                        type="number"
                        value={autoTopup.amount}
                        onChange={(e) => setAutoTopup({...autoTopup, amount: parseFloat(e.target.value)})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="1"
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={handleUpdateAutoTopup}
                  disabled={isProcessing}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
                >
                  {isProcessing ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Purchase Credits */}
          <div className="space-y-8">
            {/* Currency Selection */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Currency</h3>
              <div className="flex space-x-4">
                <button
                  onClick={() => setCurrency('NGN')}
                  className={`flex-1 py-3 rounded-xl font-medium ${
                    currency === 'NGN'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  NGN (₦)
                </button>
                <button
                  onClick={() => setCurrency('USD')}
                  className={`flex-1 py-3 rounded-xl font-medium ${
                    currency === 'USD'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  USD ($)
                </button>
              </div>
            </div>

            {/* Credit Packages */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Credit Packages</h3>
              <div className="space-y-4">
                {products.map((product) => (
                  <div 
                    key={product.product_id}
                    className={`border rounded-xl p-4 cursor-pointer transition-all ${
                      selectedProduct === product.product_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => setSelectedProduct(product.product_id)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-gray-800">{product.name}</h4>
                        <p className="text-sm text-gray-600">{product.description}</p>
                        <div className="mt-2 flex items-center space-x-2">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            {product.credits_amount} credits
                          </span>
                          <span className="text-sm text-gray-500">
                            Valid {product.valid_days} days
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-800">
                          {currency === 'NGN' 
                            ? `₦${product.price_ngn.toLocaleString()}`
                            : `$${product.price_usd.toLocaleString()}`
                          }
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePurchaseCredits(product.product_id);
                          }}
                          disabled={isProcessing}
                          className="mt-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                        >
                          Buy Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Usage Stats */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Usage Statistics</h3>
              <div className="space-y-3">
                {usage.map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Resource Type:</span>
                      <span className="font-medium text-gray-800">{item.resource_type}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Used:</span>
                      <span className="font-medium text-gray-800">{item.used_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Remaining:</span>
                      <span className="font-medium text-green-600">{item.remaining}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ 
                          width: `${Math.min(100, (item.used_count / (item.used_count + item.remaining)) * 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Support */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-xl p-6 text-white">
              <h3 className="text-lg font-bold mb-2">Need Help?</h3>
              <p className="text-blue-100 text-sm mb-4">
                Our support team is available to help with billing questions
              </p>
              <a
                href="https://wa.me/2348113315252"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 hover:bg-blue-50 py-2 px-4 rounded-lg font-medium"
              >
                <i className="fab fa-whatsapp"></i>
                WhatsApp Support
              </a>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}

export default BillingCredits;